import { v4 as uuidv4 } from 'uuid';
import type { ActivityEntrySource, ActivityMissingField } from '/@/store/tracking/events/activities';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';

/**
 * One pass through the activity form, from opening it to saving or closing it. It starts in the
 * handlers that open the form, not in the modal: the modal stays mounted and re-runs its effect
 * when an activity loads for editing.
 */
export type ActivityFlow = {
    flowId: string;
    startedAt: number;
    mode: 'create' | 'edit';
    isSquadCoach: boolean;
};

/** The properties every event of the pass carries. */
export const flowEventProps = ({ flowId, mode, isSquadCoach }: ActivityFlow) => ({
    flow_id: flowId,
    mode,
    is_squad_coach: isSquadCoach,
});

export const startActivityFlow = (
    entrySource: ActivityEntrySource,
    mode: ActivityFlow['mode'],
    isSquadCoach: boolean
): ActivityFlow => {
    const flow = { flowId: uuidv4(), startedAt: Date.now(), mode, isSquadCoach };
    mpTrackEvent('Activity flow:Started', { ...flowEventProps(flow), entry_source: entrySource });
    return flow;
};

export const secondsSince = (startedAt: number) => Math.max(0, Math.round((Date.now() - startedAt) / 1000));

/** The step-1 details still missing, in the order the form shows them. Budget and note are optional. */
export const activityMissingFields = (values: {
    activityType: string;
    date: unknown;
    name: string;
    topics: unknown[];
    speakers: unknown[];
    otherSpeakers: unknown[];
    modality: string;
    place: string;
}): ActivityMissingField[] => {
    const missing: ActivityMissingField[] = [];
    if (!values.activityType) missing.push('activity_type');
    if (!values.date) missing.push('date');
    if (!values.name.trim()) missing.push('name');
    if (values.topics.length === 0) missing.push('topics');
    if (values.speakers.length === 0 && values.otherSpeakers.length === 0) missing.push('speakers');
    if (!values.modality) missing.push('modality');
    if (values.modality === 'presencial' && !values.place.trim()) missing.push('place');
    return missing;
};

type ParticipantAttendance = { userNode: number | null; otherName?: string; attendance: string };

/** Identifies a participant across loads: by node, or by name for a free-text ("Otros") one. */
export const participantKey = ({ userNode, otherName }: Pick<ParticipantAttendance, 'userNode' | 'otherName'>) =>
    userNode !== null ? `node:${userNode}` : `other:${otherName ?? ''}`;

/**
 * Participants whose attendance differs from where it started: "Sí" for someone added now, what
 * was saved for someone already in the activity being edited.
 */
export const attendanceChangedCount = (participants: ParticipantAttendance[], initial: Map<string, string>) =>
    participants.filter((participant) => participant.attendance !== (initial.get(participantKey(participant)) ?? 'si'))
        .length;

/** What the backend said, else its status, else the error itself. */
export const errorDetail = (error: unknown): string => {
    const response = (error as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
    if (typeof response?.data?.detail === 'string') return response.data.detail;
    if (response?.status) return `HTTP ${response.status}`;
    return error instanceof Error ? error.message : String(error);
};
